import React from "react";
import { Calendar as CalendarIcon, Check, ChevronLeft, Loader2, Clock } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { api } from "@/lib/api";

/*
 * InlineBookingWidget — compact booking flow rendered inside the KAI chat.
 * Uses the same public backend endpoints as the full Contact page booking.
 *   1. pick a date          → GET /api/bookings/availability?date=…
 *   2. pick a slot + details → POST /api/bookings
 *   3. confirmation card
 */

const SERVICES = ["AI Chatbots", "Websites", "Automations", "Bookings & Reservations", "Other"];

const todayLocal = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};
const fmt = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export default function InlineBookingWidget({ onBooked }) {
  const [step, setStep] = React.useState(1); // 1=date, 2=details, 3=done
  const [date, setDate] = React.useState(undefined);
  const [slots, setSlots] = React.useState([]);
  const [loadingSlots, setLoadingSlots] = React.useState(false);
  const [time, setTime] = React.useState("");
  const [form, setForm] = React.useState({ name: "", email: "", service: "AI Chatbots" });
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [result, setResult] = React.useState(null);

  const disableDay = (d) => {
    if (!d) return true;
    if (d < todayLocal()) return true;
    const dow = d.getDay();
    return dow === 0 || dow === 6;
  };

  React.useEffect(() => {
    if (!date) return;
    setTime("");
    setLoadingSlots(true);
    api
      .get("/bookings/availability", { params: { date: fmt(date) } })
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [date]);

  const submit = async () => {
    if (!date || !time || !form.name || !form.email) {
      setErr("please fill your name and email.");
      return;
    }
    setSaving(true);
    setErr("");
    try {
      const payload = { ...form, date: fmt(date), time, notes: "booked via kai" };
      const r = await api.post("/bookings", payload);
      const booked = { ...payload, id: r.data.id };
      setResult(booked);
      setStep(3);
      onBooked?.(booked);
    } catch (e) {
      setErr(e?.response?.data?.detail || "could not save booking.");
    } finally {
      setSaving(false);
    }
  };

  // ------------------------------------------------------------- render
  return (
    <div
      className="rounded-2xl border border-blue-500/25 bg-[#0f0f18] shadow-[0_10px_30px_-14px_rgba(59,130,246,0.6)] overflow-hidden"
      data-testid="kai-inline-booking"
    >
      <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-blue-500/10">
        <CalendarIcon size={14} className="text-blue-400" />
        <p className="font-display lowercase font-bold text-sm tracking-tight text-white">
          book a discovery call
        </p>
        <span className="ml-auto text-[10px] text-gray-400">mon–fri · 30 min</span>
      </div>

      {step === 1 && (
        <div className="p-4 space-y-3">
          <div className="flex justify-center">
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={disableDay}
              className="rounded-xl border border-white/10 bg-[#12121a] p-2 text-white"
            />
          </div>

          {date && (
            <div>
              <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-2">
                <Clock size={12} /> available slots on {fmt(date)}
              </div>
              {loadingSlots ? (
                <p className="text-xs text-gray-500 inline-flex items-center gap-1">
                  <Loader2 size={12} className="animate-spin" /> loading…
                </p>
              ) : slots.length === 0 ? (
                <p className="text-xs text-gray-500">no slots left. try another day.</p>
              ) : (
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-1.5">
                  {slots.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => {
                        setTime(t);
                        setStep(2);
                      }}
                      className="text-xs py-1.5 rounded-full bg-white/5 border border-white/10 text-gray-200 hover:border-blue-500/60 hover:text-blue-300 transition"
                      data-testid={`kai-book-slot-${t}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {step === 2 && (
        <div className="p-4 space-y-3">
          <button
            type="button"
            onClick={() => setStep(1)}
            className="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-blue-300"
          >
            <ChevronLeft size={12} /> change date / time
          </button>
          <div className="text-xs text-gray-300">
            <span className="text-blue-400">{fmt(date)}</span> at{" "}
            <span className="text-blue-400">{time}</span>
          </div>
          <div className="grid grid-cols-1 gap-2">
            <input
              placeholder="your name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-blue-500/60"
              data-testid="kai-book-name"
            />
            <input
              type="email"
              placeholder="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-blue-500/60"
              data-testid="kai-book-email"
            />
            <select
              value={form.service}
              onChange={(e) => setForm((f) => ({ ...f, service: e.target.value }))}
              className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500/60"
              data-testid="kai-book-service"
            >
              {SERVICES.map((s) => (
                <option key={s} value={s} className="bg-[#12121a]">{s}</option>
              ))}
            </select>
          </div>
          {err && <p className="text-xs text-red-400">{err}</p>}
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="kyv-btn-primary w-full text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="kai-book-confirm"
          >
            {saving && <Loader2 size={14} className="animate-spin" />} confirm booking
          </button>
        </div>
      )}

      {step === 3 && result && (
        <div className="p-6 text-center space-y-2" data-testid="kai-book-success">
          <div className="p-2.5 rounded-full bg-blue-500/10 text-blue-400 w-fit mx-auto">
            <Check size={18} />
          </div>
          <p className="font-display lowercase font-bold text-xl">
            you're <span className="text-blue-500">booked.</span>
          </p>
          <p className="text-sm text-gray-300">
            {result.date} at {result.time} — confirmation sent to{" "}
            <span className="text-blue-400">{result.email}</span>.
          </p>
        </div>
      )}
    </div>
  );
}
